import {InlineDatumReactComponentProps} from '@stripe-internal/inline-data';
import {view, Icon, Tooltip} from '@sail/ui';
import {checkCircleFilled} from '@sail/icons/react/Icon';
import {defineMessage, FormattedMessage} from 'react-intl';

const messages = {
  getYouStarted: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.get_you_started',
    description: 'Get you started message',
    defaultMessage: 'I set up a few things to get you started.',
  }),
  continueCustomize: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.continue_customize',
    description: 'Continue customizing message',
    defaultMessage:
      'Keep customizing, or switch to <b>Implement</b> and complete the steps to go live.',
  }),
  pullInBrandingStyles: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.pull_in_branding_styles',
    description: 'Pull branding styles',
    defaultMessage: 'Pulled in <Tooltip>branding styles</Tooltip>',
  }),
  brandingStylesTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.branding_styles_tooltip',
    description: 'Branding styles tooltip',
    defaultMessage:
      'Logos, colors, and fonts from your website have been applied to your checkout to create a consistent brand experience.',
  }),
  addedPaymentMethodLogic: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.added_payment_method_logic',
    description: 'Added payment method logic',
    defaultMessage: 'Added <Tooltip>payment method logic</Tooltip>',
  }),
  paymentMethodLogicTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.payment_method_logic_tooltip',
    description: 'Payment method logic tooltip',
    defaultMessage:
      'Customers automatically see their preferred payment methods first based on location, local currency, device, and other signals.',
  }),
  tailoredToPricingModel: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.tailored_to_pricing_model',
    description: 'Tailored to your pricing model',
    defaultMessage: 'Tailored to <Tooltip>your pricing model</Tooltip>',
  }),
  pricingModelTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.pricing_model_tooltip',
    description: 'Pricing model tooltip',
    defaultMessage:
      'Your checkout automatically adjusts pricing displays, button text, and payment methods to match your pricing model.',
  }),
  enabledLocalizedPricing: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.enabled_localized_pricing',
    description: 'Enabled localized pricing',
    defaultMessage: 'Enabled <Tooltip>localized pricing</Tooltip>',
  }),
  enabledLocalizedPricingTooltop: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.enabled_localized_pricing_tooltip',
    description: 'Enabled localized pricing tooltip',
    defaultMessage:
      'Adaptive Pricing lets customers pay in their local currency, which can increase international revenue by up to 17%.',
  }),
};

export const CheckoutStudioGreeting = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum} = props;

  return (
    <>
      <view.div css={{stack: 'y', gap: 'medium'}}>
        <FormattedMessage {...messages.getYouStarted} />
        <view.div css={{stack: 'y', gap: 'small'}}>
          {datum?.value?.branding_style_is_eligible ? (
            <view.div css={{stack: 'x', gap: 'small'}}>
              <Icon icon={checkCircleFilled} size="xsmall" />
              <FormattedMessage
                {...messages.pullInBrandingStyles}
                values={{
                  Tooltip: (label: React.ReactNode) => (
                    <Tooltip
                      placement="top"
                      trigger={
                        <view.span css={{color: 'action.primary'}}>
                          {label}
                        </view.span>
                      }
                    >
                      <FormattedMessage {...messages.brandingStylesTooltip} />
                    </Tooltip>
                  ),
                }}
              />
            </view.div>
          ) : null}
          <view.div css={{stack: 'x', gap: 'small'}}>
            <Icon icon={checkCircleFilled} size="xsmall" />
            <FormattedMessage
              {...messages.addedPaymentMethodLogic}
              values={{
                Tooltip: (label: React.ReactNode) => (
                  <Tooltip
                    placement="top"
                    trigger={
                      <view.span css={{color: 'action.primary'}}>
                        {label}
                      </view.span>
                    }
                  >
                    <FormattedMessage {...messages.paymentMethodLogicTooltip} />
                  </Tooltip>
                ),
              }}
            />
          </view.div>
          <view.div css={{stack: 'x', gap: 'small'}}>
            <Icon icon={checkCircleFilled} size="xsmall" />
            <FormattedMessage
              {...messages.tailoredToPricingModel}
              values={{
                Tooltip: (label: React.ReactNode) => (
                  <Tooltip
                    placement="top"
                    trigger={
                      <view.span css={{color: 'action.primary'}}>
                        {label}
                      </view.span>
                    }
                  >
                    <FormattedMessage {...messages.pricingModelTooltip} />
                  </Tooltip>
                ),
              }}
            />
          </view.div>
          <view.div css={{stack: 'x', gap: 'small'}}>
            <Icon icon={checkCircleFilled} size="xsmall" />
            <FormattedMessage
              {...messages.enabledLocalizedPricing}
              values={{
                Tooltip: (label: React.ReactNode) => (
                  <Tooltip
                    placement="top"
                    trigger={
                      <view.span css={{color: 'action.primary'}}>
                        {label}
                      </view.span>
                    }
                  >
                    <FormattedMessage
                      {...messages.enabledLocalizedPricingTooltop}
                    />
                  </Tooltip>
                ),
              }}
            />
          </view.div>
        </view.div>
        <FormattedMessage
          {...messages.continueCustomize}
          values={{
            b: (chunks: React.ReactNode) => <view.b>{chunks}</view.b>,
          }}
        />
      </view.div>
    </>
  );
};
